import { trackExternalLinkOpened } from "@/utils/analytics";
import { openUrlSafely } from "@/utils/linking";
import { Ionicons } from "@expo/vector-icons";
import { Image } from "expo-image";
import { LinearGradient } from "expo-linear-gradient";
import { Stack, useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import React from "react";
import { useTranslation } from "react-i18next";
import { ScrollView, StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import type { SvgProps } from "react-native-svg";
import FerGeneveLogo from "../assets/partenaires/federation-entreprises-romandes.svg";
import RecycleursLogo from "../assets/partenaires/recycleurs-de-geneve.svg";
import VilleGeneveLogo from "../assets/partenaires/ville-geneve.svg";

// Noms propres des partenaires : jamais traduits (ce sont des marques/institutions).

// Hauteur visuelle commune à tous les logos (contain → jamais déformés).
const LOGO_HEIGHT = 60;

interface Partner {
  name: string;
  url: string;
  // Logo : soit un composant SVG (via react-native-svg-transformer), soit une
  // image bitmap (PNG uniquement — pas d'AVIF/WebP, convertis en amont).
  Logo?: React.FC<SvgProps>;
  image?: number;
}

// Ordre identique à celui du site repaschallenge.ch.
const PARTNERS: Partner[] = [
  {
    name: "Ville de Genève",
    url: "https://www.geneve.ch/",
    Logo: VilleGeneveLogo,
  },
  {
    name: "2050 Today",
    url: "https://2050today.org/",
    image: require("../assets/partenaires/2050today.png"),
  },
  {
    name: "OPAGE",
    url: "https://www.opage.ch/",
    image: require("../assets/partenaires/geneve-terroir.png"),
  },
  {
    name: "AgriGenève",
    url: "https://www.agrigeneve.ch/",
    image: require("../assets/partenaires/agrigeneve.png"),
  },
  {
    name: "ADIGE",
    url: "https://adige.ch/",
    image: require("../assets/partenaires/antenne-des-dieteticiens-genevois.png"),
  },
  {
    name: "Plan Climat cantonal",
    url: "https://www.ge.ch/dossier/durabilite-climat/climat/plan-climat-route-neutralite-carbone",
    image: require("../assets/partenaires/logo_plan_climat.png"),
  },
  {
    name: "FER Genève",
    url: "https://www.fer-ge.ch/",
    Logo: FerGeneveLogo,
  },
  {
    name: "Le Cercle des Agriculteurs",
    url: "https://cage.ch/",
    image: require("../assets/partenaires/cercle-des-agriculteurs.png"),
  },
  {
    name: "APRÈS-GE",
    url: "https://apres-ge.ch/",
    image: require("../assets/partenaires/apres-ge.png"),
  },
  {
    name: "Les Recycleurs de Genève",
    url: "https://www.lesrecycleurs.ch/",
    Logo: RecycleursLogo,
  },
  {
    name: "Fourchette verte Genève",
    url: "https://www.fourchetteverte-ge.ch/",
    image: require("../assets/partenaires/fourchette-verte.png"),
  },
  {
    name: "GastroFutura",
    url: "https://www.gastrofutura.ch/fr",
    image: require("../assets/partenaires/gastrofutura.png"),
  },
  {
    name: "Union Maraîchère de Genève (UMG)",
    url: "https://umg.ch/",
    image: require("../assets/partenaires/union-maraichere-geneve.png"),
  },
  {
    name: "ZeroWaste Genève",
    url: "https://zerowastegeneve.ch/",
    image: require("../assets/partenaires/zerowaste-geneve.png"),
  },
];

export default function PartenairesScreen() {
  const { t } = useTranslation();
  const router = useRouter();

  const openPartner = (url: string) => {
    trackExternalLinkOpened(url);
    openUrlSafely(url, {
      title: t("common.linkErrorTitle"),
      message: t("common.linkErrorMessage"),
    });
  };

  return (
    <SafeAreaView style={styles.container} edges={["top"]}>
      <StatusBar style="dark" />
      <Stack.Screen options={{ headerShown: false }} />

      <LinearGradient
        colors={["#FBEFE3", "#FFFFFF"]}
        locations={[0, 0.35]}
        style={styles.gradient}
      >
        <View style={styles.header}>
          <TouchableOpacity onPress={() => router.back()} style={styles.backButton}>
            <Ionicons name="chevron-back" size={24} color="#1C453F" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>{t("partners.headerTitle")}</Text>
          <View style={{ width: 40 }} />
        </View>

        <ScrollView
          contentContainerStyle={styles.content}
          showsVerticalScrollIndicator={false}
        >
          <Text style={styles.intro}>
            {t("partners.intro")}
          </Text>

          {PARTNERS.map((p) => (
            <TouchableOpacity
              key={p.name}
              style={styles.partnerCard}
              onPress={() => openPartner(p.url)}
              activeOpacity={0.7}
              accessibilityRole="link"
              accessibilityLabel={t("partners.openPartnerSite", { name: p.name })}
            >
              <View style={styles.logoContainer}>
                {p.Logo ? (
                  <p.Logo width="100%" height={LOGO_HEIGHT} />
                ) : (
                  <Image
                    source={p.image}
                    style={styles.logoImage}
                    contentFit="contain"
                    transition={150}
                  />
                )}
              </View>
              <View style={styles.partnerFooter}>
                <Text style={styles.partnerName}>{p.name}</Text>
                <Ionicons name="open-outline" size={18} color="#CD7728" />
              </View>
            </TouchableOpacity>
          ))}

          <View style={{ height: 40 }} />
        </ScrollView>
      </LinearGradient>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: "#FBEFE3" },
  gradient: { flex: 1 },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 16,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#FFF",
    alignItems: "center",
    justifyContent: "center",
  },
  headerTitle: { fontFamily: "Inter_700Bold", fontSize: 18, color: "#1C453F" },
  content: { paddingHorizontal: 24, paddingTop: 8 },
  intro: {
    fontFamily: "Inter_400Regular",
    fontSize: 15,
    color: "#333",
    lineHeight: 22,
    marginBottom: 24,
  },
  partnerCard: {
    // Fond blanc : plusieurs logos ont un fond transparent.
    backgroundColor: "#FFF",
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  logoContainer: {
    height: LOGO_HEIGHT,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 12,
  },
  logoImage: {
    width: "100%",
    height: LOGO_HEIGHT,
  },
  partnerFooter: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  partnerName: {
    fontFamily: "Inter_600SemiBold",
    fontSize: 14,
    color: "#1C453F",
    textAlign: "center",
    flexShrink: 1,
  },
});
